import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Where each screen was scrolled to, so going BACK returns to the row you
 * were looking at instead of the top of the list.
 *
 * The browser does this for a normal site; a single-page app has to do it
 * itself, and this one did not — every back from a booking landed at the top
 * of Orders, which on a long list means finding your place again by hand.
 *
 * Rules, which are the browser's own:
 *  - going somewhere new (PUSH) starts at the top;
 *  - going back or forward (POP) restores where that entry was left;
 *  - replacing the current entry (REPLACE — a filter written into the URL)
 *    leaves the scroll alone, since the reader has not gone anywhere.
 *
 * Positions are keyed by the history entry, so two visits to the same path
 * are two positions, and they live only for this tab's session.
 */
const positions = new Map();

/**
 * How long a restore keeps chasing the saved position while the list is still
 * arriving. Generous because the alternative — giving up early — is the bug
 * this exists to prevent, and it costs nothing: the loop stops the instant
 * the page is tall enough, or the instant the reader scrolls.
 */
const RESTORE_BUDGET_MS = 5000;

export default function ScrollMemory() {
  const location = useLocation();
  const navigationType = useNavigationType();
  // The entry being left, so its position is saved under the right key.
  const leaving = useRef(location.key);

  useEffect(() => {
    const remember = () => positions.set(leaving.current, window.scrollY);

    // The list a reader scrolls is rendered after this effect runs (the data
    // arrives later), so restoring once immediately would land on a short
    // page. Keep trying until the page is tall enough to hold the position.
    //
    // The budget is a DURATION, not a frame count. It used to be 40 frames —
    // about two thirds of a second — which a list behind a debounce and a
    // network round trip routinely misses, and then Back quietly landed at
    // the top anyway. Time is what we are actually waiting for, so time is
    // what we wait for; a frame count only measured it on a fast machine.
    //
    // Whatever happens it stops the moment the reader scrolls themselves.
    // Someone who has started reading somewhere else must not be yanked back
    // by a restore that is still hunting for a row they no longer care about.
    const restore = (target) => {
      const deadline = Date.now() + RESTORE_BUDGET_MS;
      let cancelled = false;

      const giveUp = () => { cancelled = true; };
      // `wheel` and `touchmove` are the reader's own hands. The programmatic
      // scrollTo below fires `scroll`, so `scroll` cannot be used here.
      window.addEventListener('wheel', giveUp, { passive: true, once: true });
      window.addEventListener('touchmove', giveUp, { passive: true, once: true });
      window.addEventListener('keydown', giveUp, { once: true });

      // Also cancels: the cleanup below calls this when the reader navigates
      // away mid-chase, and a loop left running would go on scrolling the
      // screen they have just moved to.
      const stop = () => {
        cancelled = true;
        window.removeEventListener('wheel', giveUp);
        window.removeEventListener('touchmove', giveUp);
        window.removeEventListener('keydown', giveUp);
      };

      const step = () => {
        if (cancelled) return stop();
        window.scrollTo(0, target);
        if (window.scrollY >= target || Date.now() >= deadline) return stop();
        return window.requestAnimationFrame(step);
      };

      window.requestAnimationFrame(step);
      return stop;
    };

    let stopRestore = null;
    if (navigationType === 'POP') {
      const target = positions.get(location.key) || 0;
      if (target > 0) stopRestore = restore(target);
    } else if (navigationType === 'PUSH') {
      window.scrollTo(0, 0);
    }

    leaving.current = location.key;
    window.addEventListener('scroll', remember, { passive: true });

    return () => {
      remember();
      window.removeEventListener('scroll', remember);
      if (stopRestore) stopRestore();
    };
  }, [location.key, navigationType]);

  return null;
}
